// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { StylesContext, useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps, StyledIcon } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TippyPopup from "@tps/App/Components/Tippy/TippyPopup";
import Menu from "@tps/MobiusOverrides/Menu/Menu";
import IconHeaderHeadsetInactive from "@tps/XDL/Icons/IconHeaderHeadsetInactive";
import xIconsObject from "@tps/XDL/Icons/xngCommonIcons";
import { color, mergeWithCss, xScreen } from "@tps/XDL/Utilities";
import * as cssLinter from "css";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

// XNG-Changes: Allow for sub-pages/chevron
interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        linkType: "Link" | "CascadingMenu";
        overriddenTitle: string;
        maxDepth: number;
        title: string;
    };
}

interface MappedLink {
    title: string;
    url: string;
    type?: string;
    openInNewWindow?: boolean;
    maxDepth?: number;
    childrenType?: "CascadingMenu";
    children?: MappedLink[];
    isCategory?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { links = [] },
    } = ownProps;
    const mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};

    for (const link of links) {
        const {
            fields: { linkType, destination, openInNewWindow, overriddenTitle, maxDepth, title },
        } = link;

        const linkMapped = getMappedLink(state, maxDepth, overriddenTitle, linkType, destination, openInNewWindow, title);
        linkMapped && mappedLinks.push(linkMapped);
    }
    return {
        links: mappedLinks,
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
    };
};

export const getMappedLink = (
    state: ApplicationState,
    maxDepth: number,
    overriddenTitle: string,
    linkType: string,
    destination: LinkFieldValue,
    openInNewWindow: boolean,
    title: string,
) => {
    const categoryIdsToLoad: Dictionary<number> = {};

    const { type, value } = destination;

    const depthToLoad = maxDepth;

    let mappedLink: MappedLink | undefined;
 // see why the overridden title is nt passed correctly
    if (type === "Url") {
        mappedLink = {
            url: value,
            title: overriddenTitle || title, // fix this
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = { ...readonlyPageLink };
            if (overriddenTitle) {
                mappedLink.title = overriddenTitle;
            }
        }
    } else if (type === "Category") {
        mappedLink = setupCategoryLink(
            state,
            value,
            categoryIdsToLoad,
            depthToLoad,
            overriddenTitle,
            state.data.categories.categoryIdsCalled,
        );
    }

    if (!mappedLink) {
        return;
    }

    mappedLink.openInNewWindow = openInNewWindow;
    mappedLink.maxDepth = maxDepth;
    mappedLink.isCategory = type === "Category";

    if (linkType === "CascadingMenu") {
        mappedLink.childrenType = linkType;
    }
    return mappedLink;
};

export const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overriddenTitle: string,
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;

    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);
    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            const categoryListPage = getPageLinkByPageType(state, "CategoryListPage");
            mappedLink = {
                url: categoryListPage?.url || "",
                title: overriddenTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overriddenTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

const mapDispatchToProps = {
    loadCategories,
    setFlyoutDrawerIsOpen,
    setNavDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // XNG-Change
    menuLink?: LinkPresentationProps;
    menuContent?: InjectableCss;
    menuContentLink?: LinkPresentationProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

export class HeaderLinkList extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        this.loadCategoriesIfNeeded();
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
    }

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad } = this.props;
        if (categoryIdsToLoad) {
            for (const categoryId of Object.keys(categoryIdsToLoad)) {
                if (!this.props.categoryErrorStatusCodeById?.[categoryId]) {
                    const maxDepth = categoryIdsToLoad[categoryId];
                    if (!this.props.categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                        this.props.loadCategories(getParameter(categoryId, maxDepth));
                    }
                }
            }
        }
    }

    closeNavigationDrawer = () => {
        const { setFlyoutDrawerIsOpen, setNavDrawerIsOpen } = this.props;
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    render() {
        const { links, fields, extendedStyles } = this.props;

        const styles = headerLinkListStyles;

        if (links.length < 1) {
            return null;
        }

        const customCssWrapper = {
            css: css`
                width: 100%;
                ${fields.customCSS}
            `,
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                    {links &&
                        links.map((link, index) => {
                            const hasChildren = link.children && link.children.length > 0;
                            const isCascading = hasChildren && link.childrenType === "CascadingMenu";
                            const image = (fields.links[index].fields as any).icon;

                            const menuLink = (expanded?: boolean) => (
                                <Link
                                    className="list-link"
                                    key={`${link.url}.${link.title}`}
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    {...(isCascading ? styles.menuLink : styles.link)}
                                    aria-expanded={expanded}
                                    aria-label={fields.links[index].fields.overriddenTitle || link.title}
                                >
                                    {/* XNG-Changes: add overriddenTitle and icon */}
                                 {image && <Icon className="prev-icon" src={xIconsObject[image]} />}   
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            );

                            let menuItem = menuLink();

                            if (isCascading && link.children) {
                                const menuProps: { maxDepth?: number } = {};
                                if (link.maxDepth) {
                                    menuProps.maxDepth = link.maxDepth;
                                }
                                const menuContent = (
                                    <StyledWrapper {...styles.menuContent}>
                                        {link.children.map((child, index) => (
                                            <Link
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`${child.url}${index}`}
                                                href={child.url}
                                                {...styles.menuContentLink}
                                                aria-label={child.title}
                                            >
                                                {child.title}
                                            </Link>
                                        ))}
                                    </StyledWrapper>
                                );
                                menuItem = (
                                    <TippyPopup
                                        content={menuContent}
                                        offset={[0, 5]}
                                        maxWidth={200}
                                        arrow={false}
                                        showCloseButton={false}
                                        appendTo="parent"
                                        placement="bottom"
                                        type="menu"
                                        onShow={this.closeNavigationDrawer}
                                        popperOptions={{
                                            strategy: "absolute",
                                            modifiers: [
                                                {
                                                    name: "preventOverflow",
                                                    options: {
                                                        padding: 0,
                                                    },
                                                },
                                                {
                                                    name: "computeStyles",
                                                    options: {
                                                        adaptive: true,
                                                        roundOffsets: ({ x, y }: any) => ({
                                                            x,
                                                            y,
                                                        }),
                                                    },
                                                },
                                            ],
                                        }}
                                    >
                                        {expanded => menuLink(expanded)}
                                    </TippyPopup>
                                );
                            }
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper className="item-wrapper" key={index}>
                                    {menuItem}
                                </StyledWrapper>
                            );
                        })}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const allIconsDropDown: { value: string; displayName: string }[] = [];
function allIconsDropDownFn() {
    Object.keys(xIconsObject).forEach(iconName => {
        allIconsDropDown.push({ value: iconName, displayName: iconName });
    });
}

allIconsDropDownFn();

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                // XNG-Change: add cascading menu option
                fieldDefinitions: [
                    {
                        name: "linkType",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "Link",
                        isRequired: true,
                        options: [
                            {
                                displayName: "Link",
                                value: "Link",
                            },
                            {
                                displayName: "Cascading Menu",
                                value: "CascadingMenu",
                            },
                        ],
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowUrls: item => item.fields.linkType === "Link",
                    },
                    // XNG-Changes: add overriddenTitle and icon
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "icon",
                        displayName: "Icon",
                        editorTemplate: "DropDownField",
                        options: allIconsDropDown,
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "maxDepth",
                        editorTemplate: "DropDownField",
                        defaultValue: 3,
                        options: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
                        isRequired: true,
                        isVisible: (item: HasFields) => item.fields.linkType === "CascadingMenu",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item: HasFields) => item.fields.linkType === "Link",
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
